/**
 * Created: 2026-02-14
 * Initial commit: Add site admin QR code feature with scene length validation (d2c7cd3)
 *
 * Dialog component for displaying action QR codes for WeChat scanning.
 * Supports cross-device authentication and action flows (login, join, addadmin).
 */

import { gql } from '@apollo/client'
import { useSubscription } from '@apollo/client/react'
import { Box, Dialog, DialogContent, LinearProgress, Typography } from '@mui/material'
import type * as React from 'react'
import { useEffect, useState } from 'react'
import type { UserBasic } from '../gql/graphql'
import { ActionQrScannedDocument, QrStatus } from '../gql/graphql'

const DEFAULT_TIMEOUT_SECONDS = 120

gql`
  subscription ActionQrScanned($qrid: ID!) {
    actionQrScanned(qrid: $qrid) {
      qrid
      status
      files
      user {
        id
        openid
        name
        avatar
      }
    }
  }
`

interface ActionQrDialogProps {
  open: boolean
  onClose?: () => void
  action: string
  actionId?: string
  title: string
  onScanned?: (user: Pick<UserBasic, 'id' | 'openid' | 'name' | 'avatar'>) => void
  /**
   * Called when the scanning user has completed the action on her device, with
   * whatever files it produced.
   *
   * Only an action that stores files reports this — `upload` today. The dialog
   * does not close itself on it: what to do next belongs to the caller, which
   * may want to keep it open for another round.
   */
  onConfirmed?: (files: string[]) => void
  extraInfo?: string
  /** Seconds before the QR code expires and the dialog closes. Defaults to 120. */
  timeoutSeconds?: number
}

/**
 * A dialog that displays an action QR code for users to scan with WeChat
 */
const ActionQrDialog: React.FC<ActionQrDialogProps> = ({
  open,
  onClose,
  action,
  actionId,
  title,
  onScanned,
  onConfirmed,
  extraInfo = '',
  timeoutSeconds = DEFAULT_TIMEOUT_SECONDS,
}) => {
  const [qrUrl, setQrUrl] = useState<string>()
  const [qrid, setQrid] = useState<string>()
  const [secondsLeft, setSecondsLeft] = useState(timeoutSeconds)
  const [scanned, setScanned] = useState(false)

  const { data } = useSubscription(ActionQrScannedDocument, {
    variables: { qrid: qrid as string },
    skip: !qrid || !open,
  })

  useEffect(() => {
    if (!data?.actionQrScanned) return

    const { status, user, files } = data.actionQrScanned
    if (status === QrStatus.Scanned && user?.openid) {
      setScanned(true)
      onScanned?.(user)
    }
    /**
     * An empty list still fires: the user confirmed, and a caller waiting on
     * this needs to stop waiting either way.
     */
    if (status === QrStatus.Confirmed) {
      onConfirmed?.(files)
    }
  }, [data, onScanned, onConfirmed])

  useEffect(() => {
    if (!open || scanned) return

    setSecondsLeft(timeoutSeconds)
    const intervalId = setInterval(() => {
      setSecondsLeft((prev) => Math.max(prev - 1, 0))
    }, 1000)
    return () => clearInterval(intervalId)
  }, [open, timeoutSeconds, scanned])

  useEffect(() => {
    if (!open) {
      setScanned(false)
    }
  }, [open])

  useEffect(() => {
    if (open && !scanned && secondsLeft === 0) {
      onClose?.()
    }
  }, [open, scanned, secondsLeft, onClose])

  useEffect(() => {
    if (!open) {
      setQrid(undefined)
      return
    }

    let objectUrl: string

    const fetchImage = async () => {
      const authToken = localStorage.getItem('authToken')
      const headers: HeadersInit = {}

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`
      }

      const params = new URLSearchParams({ action })
      if (actionId) params.set('action_id', actionId)
      if (extraInfo) params.set('extra_info', extraInfo)

      const response = await fetch(`/api/actionqr?${params}`, { headers })
      objectUrl = URL.createObjectURL(await response.blob())
      const qrId = response.headers.get('Action-QR-Id')

      setQrUrl(objectUrl)
      if (qrId) {
        setQrid(qrId)
      }
    }

    fetchImage()

    // Cleanup function: Revoke the Blob URL when the component unmounts
    // or when the dialog closes to prevent memory leaks.
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
        setQrUrl(undefined)
      }
    }
  }, [open, action, actionId, extraInfo])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paperWidthSm': { width: 350 },
        marginLeft: 'auto',
        marginRight: 'auto',
        mt: 6,
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" component="div" sx={{ textAlign: 'center', mb: 2 }}>
            {title}
          </Typography>

          {qrUrl && !scanned && (
            <Box
              component="img"
              src={qrUrl}
              alt="action qr code"
              data-testid="qr-code-image"
              sx={{
                width: 200,
                height: 200,
                borderRadius: 2,
                boxShadow: 3,
                cursor: 'pointer',
              }}
            />
          )}

          {qrUrl && !scanned && (
            <Box sx={{ width: 200, mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={(secondsLeft / timeoutSeconds) * 100}
                color="success"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
              >
                二维码将在 {secondsLeft} 秒后失效
              </Typography>
            </Box>
          )}

          {scanned && action === 'upload' && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body1" color="text.primary" sx={{ mb: 1 }}>
                已扫码，等待上传...
              </Typography>
              <LinearProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ActionQrDialog
